import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Select  from 'react-select';
import { useNavigate } from 'react-router-dom';
import { addDevice, editDevice } from '@modules/devices/utils/devices';
import { FormAddDevice, FormDevicesProps, newDeviceProps } from '@modules/devices/types/types-devices';
import FormButtons from '@modules/devices/components/form-new-device/form-components/FormButtons';
import * as yup from 'yup';
import Selector from '@components/selector/selector';
import BackTitlePrimary from "@components/back-buttons/back-title-primary";

const FormDevices = ( props: FormDevicesProps) => {
  const {onToggle, offices, deviceToEdit, isEditing, organizationHostId, deviceTypes} = props;
  const { t } = useTranslation(['myDevices']);
  const navigate = useNavigate();
  const history = useNavigate();

  const officesSelected = useMemo(() => {
    return offices.map((consult) => {
      return {
        label: consult.practitionerOfficeName,
        value: consult.practitionerOfficeName,
      }
    })
  }, [offices]);

  const onSubmit:SubmitHandler<FormAddDevice> = async (data) => {
    const organizationId = offices.filter(office => office.practitionerOfficeName === data.consultingRoom);
    const dataToSend:newDeviceProps = {
      deviceId: isEditing && deviceToEdit ? deviceToEdit.deviceId : parseInt(uuidv4()),
      deviceType: {
        code: data.name,
        description: 'empty code',
        deviceTypeId: 10
      },
      identification: data.id,
      model: data.model,
      nestedOrganizationStructure: {
        //TODO get reals data
        nestedOrganizationId: organizationId[0].legacyId || 0,
        organizationName: data.consultingRoom,
        officeId:organizationId[0].id
      },
      organizationHostId: organizationHostId,
      serialNumber: data.serialN
    }
    if(isEditing)
      await editDevice(dataToSend);
    else
      await addDevice(dataToSend);

    setCleanForm();
  }

  const setCleanForm = () => {
    onToggle();
  }
  const identificationRegex = /^[A-Za-z0-9]*$/
  const schema = yup.object().shape({
    id: yup.string().required(t('form.errors.idDevice')).matches(identificationRegex, t('form.errors.identificationRegex')),
    name: yup.string().required(t('form.errors.selectDevice')),
    model: yup.string(),
    serialN: yup.string(),
    consultingRoom: yup.string().required(t('form.errors.selectRoom')),
    // consultingRoom: yup.string()
  }).required();

  const { register, control, handleSubmit , formState: { errors }, setValue} = useForm<FormAddDevice>({
    resolver: yupResolver(schema)
  })

  // eslint-disable-next-line
  const [optionsConsultingRoom, setOptionsConsultingRoom] = useState<any>();

  const officesFiltered = useCallback(async () => {
    const optionsOffice = offices.map(office => ({
      value: office.practitionerOfficeName,
      label: office.practitionerOfficeName
    }))

    setOptionsConsultingRoom(optionsOffice);
  }, [offices])

  useEffect(() => {
    officesFiltered();
  }, [officesFiltered])

  const setValues = useCallback((deviceToEdit: newDeviceProps) =>{
    setValue( 'name', deviceToEdit.deviceType.code );
    setValue( 'id', deviceToEdit.identification);
    setValue( 'model', deviceToEdit.model);
    setValue( 'serialN', deviceToEdit.serialNumber);
    setValue( 'consultingRoom', deviceToEdit.nestedOrganizationStructure.organizationName);
  }, [setValue])
  
  useEffect(() => {
      if(deviceToEdit){
        setValues(deviceToEdit)
      }
  }, [deviceToEdit, setValues])

  return (
    <>
      <BackTitlePrimary titleText= {t('add')} backAction={() => history(-1)}/>
      <form onSubmit={handleSubmit(onSubmit)} className='form-margin justify-center'>
          <div className="form-field">
            <label
              className="Body2-600"
              htmlFor="code"
              data-end="*"
            >
              {t('form.select')}
            </label>
            <Controller
              control={control}
              name="name"
              render={({
                  field: { onChange, value, name },
                }) => (
                <Selector options={[
                  {
                    value: "",
                    option: t("form.select")
                  },
                  ...deviceTypes
                ]}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  value={value} />
              )}
            /> <br/>
            {errors.name?.message && (
              <span className="Error" role="alert">
                  {errors.name?.message}
              </span>
            )}
          </div>
          
          <div className="form-field">
            <label
              className="Body2-600"
              htmlFor="code"
            >
              {t('form.model')}
            </label>
            <input
              maxLength={64}
              defaultValue={isEditing && deviceToEdit ? deviceToEdit.model : ''}
              {...register('model')}
              className="input-primary"
            />
          </div>

          <div className="form-field">
            <label
              className="Body2-600"
              htmlFor="code"
            >
              {t('form.consultRoom')}
            </label>
            {
              // !initForm.consultingRoom.length
              !offices.length
              ? (
                  <div
                        className="Profile__FlexItem-NoPadding"
                        onClick={() => navigate('/practitioner/office')}
                  >
                    <button className="ButtonTertiary">
                      <p className="Body2-600">
                        {t('addRoom')}
                      </p>
                      <i className="far fa-plus" />
                    </button>
                  </div>
                )
              : (
                  <>
                    <Controller
                      control={control}
                      name="consultingRoom"
                      render={({ field: { onChange, value, name }, }) => (
                        <Select
                          defaultInputValue={isEditing && deviceToEdit
                            ? deviceToEdit?.nestedOrganizationStructure.organizationName : undefined}
                          name={name}
                          onChange={(selectedOption) => {onChange(selectedOption?.value)}}
                          options={officesSelected}
                        />
                      )}
                    />
                    <br/>
                    {errors.consultingRoom?.message && (
                      <span className="Error" role="alert">
                        {errors.consultingRoom?.message}
                    </span>
                    )}
                  </>
                )
            }
          </div>
      
          <div className="form-field">
            <div className='VerticalFlex__Gap'>
              <div className="Alert__GlowBlue">
                  <i className={`far fa-info-circle`} />
                  <p className="Body2"> {t('form.info')} </p>
              </div>
              <div className="form-field">
                <label
                  className="Body2-600"
                  htmlFor="code"
                  data-end="*"
                >
                  {t('form.id')}
                </label>
                <Controller
                  name='id'
                  control={control}
                  render={({ field: { onChange, value, name }, }) => (
                    <input
                      name={name}
                      maxLength={64}
                      defaultValue={isEditing && deviceToEdit ? deviceToEdit.identification : ''}
                      onChange={onChange}
                      className="input-primary"
                      value={value}
                    />
                  )}
                />
                <br/>
                {errors.id?.message && (
                  <span className="Error" role="alert">
                      {errors.id?.message}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label
                className="Body2-600"
                htmlFor="code"
              >
                {t('form.serialN')}
              </label>
              <input
                maxLength={64}
                defaultValue={isEditing && deviceToEdit ? deviceToEdit.serialNumber : ''}
                {...register('serialN')}
                className="input-primary"
              />
            </div>

  
            {/* <p className="BlueText">
              {' '}
              (*) {t('form.required')}
            </p> */}
          </div>
        <FormButtons setCleanForm={setCleanForm} />
      </form>
    </>
  );
};

export default FormDevices;
